import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Document types with display names
const DOCUMENT_TYPES = {
  terms_of_service: 'Terms of Service',
  privacy_policy: 'Privacy Policy',
  acceptable_use_policy: 'Acceptable Use Policy',
  service_level_agreement: 'Service Level Agreement',
  data_processing_agreement: 'Data Processing Agreement',
  billing_refund_policy: 'Billing & Refund Policy',
  custom: 'Custom Document'
};

const STATUS_BADGES = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
  published: { bg: 'bg-green-100', text: 'text-green-800', label: 'Published' },
  archived: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Archived' }
};

/**
 * LegalDocumentsManagement - Admin portal for managing legal documents
 *
 * Features:
 * - Create, edit, publish, archive legal documents
 * - Version management with semantic versioning
 * - Multi-language support
 * - View acceptance statistics
 * - Markdown editor with preview
 */
const LegalDocumentsManagement = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // 'create', 'edit', 'version'
  const [filter, setFilter] = useState({ status: 'all', type: 'all' });
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    type: 'terms_of_service',
    name: '',
    slug: '',
    version: '1.0.0',
    language: 'en',
    content_markdown: '',
    summary: '',
    key_points: [],
    checkbox_text: '',
    effective_date: '',
    requires_re_acceptance: false,
    applicable_to_subscription_approval: true,
    is_mandatory: true,
    display_order: 0
  });
  const [keyPointInput, setKeyPointInput] = useState('');

  const isSuperadmin = user?.role === 'superadmin';
  const hasPermission = user?.admin_permissions?.includes('legal_documents');
  const canAccessLegalDocs = isSuperadmin || hasPermission;

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status !== 'all') params.append('status', filter.status);
      if (filter.type !== 'all') params.append('type', filter.type);

      const response = await axios.get(
        `${getApiBase()}/admin/legal/documents?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setDocuments(response.data.documents || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleCreateNew = () => {
    setEditorMode('create');
    setFormData({
      type: 'terms_of_service',
      name: '',
      slug: '',
      version: '1.0.0',
      language: 'en',
      content_markdown: '',
      summary: '',
      key_points: [],
      checkbox_text: 'I have read and agree to the Terms of Service',
      effective_date: '',
      requires_re_acceptance: false,
      applicable_to_subscription_approval: true,
      is_mandatory: true,
      display_order: 0
    });
    setSelectedDocument(null);
    setShowEditor(true);
  };

  const handleEdit = (doc) => {
    setEditorMode('edit');
    setFormData({
      type: doc.type,
      name: doc.name,
      slug: doc.slug,
      version: doc.version,
      language: doc.language,
      content_markdown: doc.content_markdown,
      summary: doc.summary || '',
      key_points: doc.key_points || [],
      checkbox_text: doc.checkbox_text || '',
      effective_date: doc.effective_date ? doc.effective_date.split('T')[0] : '',
      requires_re_acceptance: doc.requires_re_acceptance,
      applicable_to_subscription_approval: doc.applicable_to_subscription_approval,
      is_mandatory: doc.is_mandatory,
      display_order: doc.display_order || 0
    });
    setSelectedDocument(doc);
    setShowEditor(true);
  };

  const handleCreateNewVersion = (doc) => {
    setEditorMode('version');
    // Increment version
    const versionParts = doc.version.split('.');
    const newVersion = `${parseInt(versionParts[0]) + 1}.0.0`;

    setFormData({
      type: doc.type,
      name: doc.name,
      slug: doc.slug,
      version: newVersion,
      language: doc.language,
      content_markdown: doc.content_markdown,
      summary: doc.summary || '',
      key_points: doc.key_points || [],
      checkbox_text: doc.checkbox_text || '',
      effective_date: '',
      requires_re_acceptance: true,
      applicable_to_subscription_approval: doc.applicable_to_subscription_approval,
      is_mandatory: doc.is_mandatory,
      display_order: doc.display_order || 0
    });
    setSelectedDocument(doc);
    setShowEditor(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        effective_date: formData.effective_date || null
      };

      if (editorMode === 'create') {
        await axios.post(
          `${getApiBase()}/admin/legal/documents`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else if (editorMode === 'edit' && selectedDocument) {
        await axios.put(
          `${getApiBase()}/admin/legal/documents/${selectedDocument.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
      } else if (editorMode === 'version' && selectedDocument) {
        await axios.post(
          `${getApiBase()}/admin/legal/documents/${selectedDocument.id}/create-version`,
          payload,
          { headers: getAuthHeaders() }
        );
      }

      setShowEditor(false);
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (doc) => {
    if (!window.confirm(`Are you sure you want to publish "${doc.name}" v${doc.version}? This will make it visible to users.`)) {
      return;
    }

    try {
      await axios.post(
        `${getApiBase()}/admin/legal/documents/${doc.id}/publish`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to publish document');
    }
  };

  const handleArchive = async (doc) => {
    if (!window.confirm(`Are you sure you want to archive "${doc.name}" v${doc.version}? Archived documents won't be shown to new users.`)) {
      return;
    }

    try {
      await axios.post(
        `${getApiBase()}/admin/legal/documents/${doc.id}/archive`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to archive document');
    }
  };

  const handleAddKeyPoint = () => {
    if (keyPointInput.trim()) {
      setFormData(prev => ({
        ...prev,
        key_points: [...prev.key_points, keyPointInput.trim()]
      }));
      setKeyPointInput('');
    }
  };

  const handleRemoveKeyPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };

  const generateSlug = (name, type) => {
    if (!name) {
      // Use type for default slug
      return type.replace(/_/g, '-');
    }
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  // Access check
  if (!canAccessLegalDocs) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-white rounded-lg shadow">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to manage legal documents.
            Requires superadmin access or Legal Documents permission.
          </p>
        </div>
      </div>
    );
  }

  // Editor view
  if (showEditor) {
    return (
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div className="bg-white rounded-lg shadow">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {editorMode === 'create' ? 'Create New Document' :
                 editorMode === 'version' ? 'Create New Version' :
                 'Edit Document'}
              </h2>
              <button
                onClick={() => setShowEditor(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <div className="p-6 space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      type,
                      name: DOCUMENT_TYPES[type] || '',
                      slug: generateSlug('', type),
                      checkbox_text: `I have read and agree to the ${DOCUMENT_TYPES[type] || 'document'}`
                    }));
                  }}
                  disabled={editorMode !== 'create'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  {Object.entries(DOCUMENT_TYPES).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Document Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      name: e.target.value,
                      slug: generateSlug(e.target.value, prev.type)
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Terms of Service"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Slug
                </label>
                <div className="flex items-center">
                  <span className="text-gray-500 text-sm mr-2">/legal/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="terms-of-service"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Version <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="1.0.0"
                />
                <p className="mt-1 text-xs text-gray-500">Semantic versioning (e.g., 1.0.0, 2.1.0)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="pt">Portuguese</option>
                  <option value="ja">Japanese</option>
                  <option value="zh">Chinese</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Effective Date
                </label>
                <input
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">When this version becomes effective</p>
              </div>
            </div>

            {/* Summary */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Summary
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="A brief summary of this document..."
              />
            </div>

            {/* Key Points */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Key Points
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={keyPointInput}
                  onChange={(e) => setKeyPointInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyPoint())}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add a key point..."
                />
                <button
                  type="button"
                  onClick={handleAddKeyPoint}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              {formData.key_points.length > 0 && (
                <ul className="space-y-1">
                  {formData.key_points.map((point, index) => (
                    <li key={index} className="flex items-center justify-between py-1 px-3 bg-gray-50 rounded">
                      <span className="text-sm text-gray-700">{point}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyPoint(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Checkbox Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Checkbox Consent Text
              </label>
              <input
                type="text"
                value={formData.checkbox_text}
                onChange={(e) => setFormData(prev => ({ ...prev, checkbox_text: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="I have read and agree to the Terms of Service"
              />
              <p className="mt-1 text-xs text-gray-500">Text shown next to the acceptance checkbox</p>
            </div>

            {/* Content Editor */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Document Content (Markdown) <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>

              <div className={`grid ${showPreview ? 'grid-cols-2 gap-4' : 'grid-cols-1'}`}>
                <textarea
                  value={formData.content_markdown}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_markdown: e.target.value }))}
                  rows={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="# Terms of Service&#10;&#10;## 1. Introduction&#10;&#10;Welcome to Veyage..."
                />
                {showPreview && (
                  <div className="border border-gray-300 rounded-lg p-4 overflow-auto max-h-[500px] prose prose-sm">
                    <div dangerouslySetInnerHTML={{
                      __html: formData.content_markdown
                        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
                        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
                        .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
                        .replace(/\*(.*)\*/gim, '<em>$1</em>')
                        .replace(/^\- (.*)$/gim, '<li>$1</li>')
                        .replace(/\n/gim, '<br />')
                    }} />
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.applicable_to_subscription_approval}
                    onChange={(e) => setFormData(prev => ({ ...prev, applicable_to_subscription_approval: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show during subscription approval</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_mandatory}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_mandatory: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Mandatory acceptance required</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.requires_re_acceptance}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_re_acceptance: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Require re-acceptance when updated</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
                <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowEditor(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.content_markdown}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {editorMode === 'create' ? 'Create Document' :
                 editorMode === 'version' ? 'Create New Version' :
                 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Legal Documents</h1>
              <p className="text-gray-600 mt-1">Manage terms of service, privacy policy, and other legal documents</p>
            </div>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Document
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={filter.status}
                onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select
                value={filter.type}
                onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {Object.entries(DOCUMENT_TYPES).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading documents...</p>
          </div>
        )}

        {/* Documents List */}
        {!loading && documents.length === 0 && (
          <div className="p-8 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No documents found</h3>
            <p className="text-gray-600 mb-4">Get started by creating your first legal document</p>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Document
            </button>
          </div>
        )}

        {!loading && documents.length > 0 && (
          <div className="divide-y divide-gray-200">
            {documents.map((doc) => {
              const statusBadge = STATUS_BADGES[doc.status] || STATUS_BADGES.draft;
              return (
                <div key={doc.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{doc.name}</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          v{doc.version}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {DOCUMENT_TYPES[doc.type]} • {doc.language.toUpperCase()}
                      </p>
                      {doc.summary && (
                        <p className="text-sm text-gray-500 line-clamp-2">{doc.summary}</p>
                      )}
                      <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                        {doc.effective_date && (
                          <span>Effective: {new Date(doc.effective_date).toLocaleDateString()}</span>
                        )}
                        <span>Created: {new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.published_at && (
                          <span>Published: {new Date(doc.published_at).toLocaleDateString()}</span>
                        )}
                        {doc.applicable_to_subscription_approval && (
                          <span className="text-blue-600">Shown during approval</span>
                        )}
                        {doc.is_mandatory && (
                          <span className="text-orange-600">Mandatory</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleEdit(doc)}
                        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>

                      {doc.status === 'draft' && (
                        <button
                          onClick={() => handlePublish(doc)}
                          className="inline-flex items-center px-3 py-1.5 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Publish
                        </button>
                      )}

                      {doc.status === 'published' && (
                        <>
                          <button
                            onClick={() => handleCreateNewVersion(doc)}
                            className="inline-flex items-center px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                            New Version
                          </button>
                          <button
                            onClick={() => handleArchive(doc)}
                            className="inline-flex items-center px-3 py-1.5 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                            Archive
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalDocumentsManagement;
